import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
} from 'reactstrap';
import { toast } from 'react-toastify';

import { ODataGrid } from '../../../../../Components/OElements/ODataGrid';
import { getGuideDetailList } from '../../../../../slices/guideDetail/thunk';
import { committedKgByGuideDetailIdFromItems } from '../../utils/warehouseGuideLinesSendAvailability';

const formatKg = value => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '-';
	return n.toLocaleString('es-CL', { maximumFractionDigits: 2 });
};

const isMongoObjectIdString = value =>
	typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

const guideDetailSelector = createSelector(
	state => state.GuideDetails,
	gd => ({
		guideDetailList: gd.guideDetailList,
		isLoadingList: gd.isLoadingList,
	}),
);

const KgToSendCell = React.memo(function KgToSendCell({
	rowId,
	cap,
	selected,
	override,
	onCommit,
}) {
	const [str, setStr] = useState('');

	useEffect(() => {
		if (!selected) {
			setStr('');
			return;
		}
		if (override !== undefined && Number.isFinite(Number(override))) {
			setStr(String(Number(override)));
			return;
		}
		setStr(String(Number(cap)));
	}, [selected, cap, override, rowId]);

	if (!selected) {
		return <span className='text-muted small'>—</span>;
	}

	const parseLoose = raw => {
		const n = parseFloat(String(raw).replace(',', '.'));
		return Number.isFinite(n) ? n : NaN;
	};

	const clamp = n => {
		let x = n;
		if (!Number.isFinite(x) || x <= 0) x = Number(cap);
		if (x > Number(cap)) x = Number(cap);
		return x;
	};

	return (
		<input
			type='number'
			className='form-control form-control-sm'
			style={{ maxWidth: '140px' }}
			min={0}
			step={0.01}
			max={cap}
			value={str}
			onChange={e => {
				const v = e.target.value;
				setStr(v);
				if (v === '' || v === '-') {
					onCommit(rowId, undefined);
					return;
				}
				const n = parseLoose(v);
				if (!Number.isFinite(n)) return;
				if (n > 0 && n <= Number(cap)) {
					onCommit(rowId, n);
				}
			}}
			onBlur={() => {
				const n = clamp(parseLoose(str));
				setStr(String(n));
				onCommit(rowId, n);
			}}
		/>
	);
});

/**
 * Picker de líneas de guía recepcionadas (NP) para envío desde bodega en el generador.
 */
const WarehouseGuideDetailPickerModal = ({
	isOpen,
	toggle,
	residueTypeId,
	establishmentId,
	onConfirm,
	existingGuideItems = [],
}) => {
	const dispatch = useDispatch();
	const { guideDetailList, isLoadingList } = useSelector(guideDetailSelector);

	const committedByDetailId = useMemo(
		() => committedKgByGuideDetailIdFromItems(existingGuideItems),
		[existingGuideItems],
	);

	const listWithAvailability = useMemo(() => {
		return (guideDetailList || [])
			.map(item => {
				const id = String(item._id);
				const serverAvail = Number(item.available_kilograms_send) || 0;
				const committed = Number(committedByDetailId[id]) || 0;
				const available = Math.max(0, serverAvail - committed);
				return {
					...item,
					_id: id,
					kilograms_available: available,
					kilograms_total_server: serverAvail,
					kilograms_in_guide: committed,
				};
			})
			.filter(row => row.kilograms_available > 0);
	}, [guideDetailList, committedByDetailId]);

	const canFetch =
		isOpen &&
		isMongoObjectIdString(residueTypeId) &&
		isMongoObjectIdString(establishmentId);

	useEffect(() => {
		if (!isOpen) return;
		if (!canFetch) return;
		dispatch(
			getGuideDetailList({
				send_warehouse: true,
				residue_type_id: residueTypeId,
				warehouse_establishment_id: establishmentId,
			}),
		);
	}, [isOpen, canFetch, residueTypeId, establishmentId, dispatch]);

	const [selectionList, setSelectionList] = useState({});
	const [kgOverrides, setKgOverrides] = useState({});

	useEffect(() => {
		if (!isOpen) {
			setSelectionList({});
			setKgOverrides({});
		}
	}, [isOpen]);

	useEffect(() => {
		setKgOverrides(prev => {
			const next = { ...prev };
			for (const k of Object.keys(next)) {
				if (!selectionList[k]) delete next[k];
			}
			return next;
		});
	}, [selectionList]);

	const commitKg = useCallback((rowId, n) => {
		setKgOverrides(prev => {
			const next = { ...prev };
			if (n === undefined || !Number.isFinite(Number(n))) {
				delete next[rowId];
			} else {
				next[rowId] = Number(n);
			}
			return next;
		});
	}, []);

	const linesSelected = useMemo(() => {
		return listWithAvailability
			.filter(item => selectionList[item._id])
			.map(item => {
				const override = kgOverrides[item._id];
				const defaultKg = item.kilograms_available;
				const kg =
					override !== undefined && Number.isFinite(Number(override))
						? Number(override)
						: defaultKg;
				return {
					...item,
					kilograms_to_send: kg,
				};
			});
	}, [listWithAvailability, selectionList, kgOverrides]);

	const columns = useMemo(
		() => [
			{
				header: 'Guía',
				accessorKey: 'guide.folio',
				size: 'auto',
				Cell: ({ row }) => {
					const g = row.original.guide;
					return (
						g?.folio ||
						g?.guide_number ||
						row.original.guide_number ||
						'-'
					);
				},
			},
			{
				header: 'Fecha',
				accessorKey: 'guide.guide_date',
				dataType: 'date',
				size: 'auto',
			},
			{
				header: 'Residuo',
				accessorKey: 'residue.name',
				size: 'auto',
				Cell: ({ row }) =>
					row.original.residue?.name || row.original.residue_id || '-',
			},
			{
				header: 'Kg totales',
				accessorKey: 'kilograms_total',
				size: 'auto',
				Cell: ({ row }) => formatKg(row.original.kilograms_total),
			},
			{
				header: 'Kg disponibles',
				accessorKey: 'kilograms_available',
				size: 'auto',
				Cell: ({ row }) => formatKg(row.original.kilograms_available),
			},
			{
				header: 'Kg a enviar',
				accessorKey: 'kilograms_to_send_draft',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				size: 'auto',
				Cell: ({ row }) => {
					const id = row.original._id;
					const cap = Number(row.original.kilograms_available) || 0;
					return (
						<KgToSendCell
							rowId={id}
							cap={cap}
							selected={!!selectionList[id]}
							override={kgOverrides[id]}
							onCommit={commitKg}
						/>
					);
				},
			},
		],
		[selectionList, kgOverrides, commitKg],
	);

	const handleConfirm = () => {
		if (linesSelected.length === 0) {
			toast.error('Debes seleccionar al menos una línea de guía para enviar.');
			return;
		}
		const invalid = linesSelected.find(item => {
			const kg = Number(item.kilograms_to_send);
			const cap = Number(item.kilograms_available);
			return (
				!Number.isFinite(kg) ||
				kg <= 0 ||
				!Number.isFinite(cap) ||
				kg > cap
			);
		});
		if (invalid) {
			toast.error(
				`Kilogramos inválidos para ${invalid.residue?.name || 'la línea seleccionada'}.`,
			);
			return;
		}
		onConfirm(linesSelected);
	};

	const selectedCount = Object.keys(selectionList).filter(
		k => selectionList[k],
	).length;

	return (
		<Modal
			isOpen={isOpen}
			toggle={toggle}
			size='xl'
			backdrop='static'
			centered
			className='border-0'
			modalClassName='modal fade zoomIn'
		>
			<ModalHeader toggle={toggle}>
				Agregar desde guías recepcionadas
			</ModalHeader>
			<ModalBody>
				<p className='text-muted small mb-3'>
					Líneas de guías no peligrosas recepcionadas en bodegas de este
					establecimiento. Los kg a enviar no pueden superar el disponible por
					línea.
				</p>
				{!canFetch ? (
					<p className='text-warning small'>
						Seleccione el establecimiento de origen antes de abrir este
						listado.
					</p>
				) : null}
				<ODataGrid
					columns={columns}
					data={listWithAvailability}
					getRowIdTable={originalRow => originalRow._id}
					selectAllMode='all'
					selection={{
						rowSelection: selectionList,
						setRowSelection: setSelectionList,
					}}
					isLoadingData={isLoadingList}
				/>
			</ModalBody>
			<ModalFooter className='gap-2'>
				<Button type='button' color='light' onClick={toggle}>
					Cancelar
				</Button>
				<Button
					type='button'
					color='primary'
					onClick={handleConfirm}
					disabled={selectedCount === 0 || !canFetch}
				>
					<i className='ri-check-line me-1'></i>
					Confirmar
					{selectedCount > 0 ? ` (${selectedCount})` : ''}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default WarehouseGuideDetailPickerModal;
